import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getRatePlansByProperty, getRoomsByProperty } from "../../../api";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import {
  buildAllRoomRateplanCombos,
  filterCombosForProperty,
  normalizePropertyKey,
  resolveComboOwner,
} from "../utils/roomRateCombos";

const LOG_SOURCE = "useOfferTypeRoomsRateplans";

/**
 * Sub-hook of `useOfferTypeState` owning all rooms/rate-plans drawer state,
 * the combo-fetch logic, and the effects that keep drawer data in sync with
 * the promo field and external props.
 *
 * Inputs come from the orchestrator's outer props plus the values returned
 * by `useOfferTypePropertySelection`.
 */
export function useOfferTypeRoomsRateplans({
  promo,
  onFieldChange,
  ratePlans,
  rooms,
  isMarket,
  selectedProperty,
  selectedProperties,
  setSelectedProperties,
  // Per-property rooms/rate-plans cache — owned by the orchestrator so the
  // property-selection sub-hook can clear it on a header property change.
  // Optional: standalone use of this hook falls back to an internal ref.
  roomsRateplansByPropertyRef: externalRoomsRateplansByPropertyRef,
  // Property that `ratePlans`/`rooms` were fetched for. Used to tag the
  // combos built from them so a selection can be matched back to its owner.
  ratePlansPropertyId = null,
  // Flows that hide the "All Rooms & Rate Plans" choice (e.g. campaign
  // approve) should pass true. When set, the hook never sets
  // roomsRateplansValue to "all" — the empty case and the "every combo
  // selected" case both fall through to "select" so the filtered dropdown
  // always has a matching option to render.
  forceSelectOnly = false,
}) {
  // -------------------------------------------------------------------------
  // Refs (private)
  // -------------------------------------------------------------------------
  const internalRoomsRateplansByPropertyRef = useRef({});
  const roomsRateplansByPropertyRef =
    externalRoomsRateplansByPropertyRef ?? internalRoomsRateplansByPropertyRef;
  const savedSelectionsBeforeDrawerRef = useRef(null);
  const prevDrawerOpenRef = useRef(false);
  const selectedRoomsRateplansRef = useRef(promo?.allowedRateRoomCodes || []);
  const selectedRoomsRateplansKeyRef = useRef(
    JSON.stringify(promo?.allowedRateRoomCodes || [])
  );
  // Latest property whose data was requested, so an earlier, slower response
  // cannot overwrite a newer selection.
  const pendingSinglePropertyRef = useRef(null);

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [showRoomsRateplansModal, setShowRoomsRateplansModal] = useState(false);
  const [drawerSelections, setDrawerSelections] = useState([]);
  const [drawerRatePlans, setDrawerRatePlans] = useState(ratePlans || []);
  const [drawerRooms, setDrawerRooms] = useState(rooms || []);
  // Property the grid inside the drawer is currently showing. The drawer can
  // switch hotels, so this is not always the header property.
  const [drawerDataPropertyId, setDrawerDataPropertyId] =
    useState(ratePlansPropertyId);
  const [selectedHotelInDrawer, setSelectedHotelInDrawer] = useState(() => {
    if (
      selectedProperties.length > 1 &&
      !selectedProperties.includes(selectedProperty?.propertyId)
    ) {
      return selectedProperties[0] || null;
    }
    return selectedProperty?.propertyId || null;
  });
  const [successMessage, setSuccessMessage] = useState(null);
  const [roomsRateplansValue, setRoomsRateplansValue] = useState(() => {
    if (forceSelectOnly) return "select";
    const initial = promo?.allowedRateRoomCodes || [];
    if (initial.length === 0) {
      return "all";
    }
    const totalCombos = (rooms?.length || 0) * (ratePlans?.length || 0);
    return initial.length === totalCombos ? "all" : "select";
  });

  // -------------------------------------------------------------------------
  // Memos
  // -------------------------------------------------------------------------
  const roomsRateplansOptions = useMemo(() => {
    return [
      { label: "All Rooms & Rate Plans", value: "all" },
      { label: "Selected Rooms & Rate Plans", value: "select" },
    ];
  }, []);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleDrawerClose = useCallback(() => {
    if (savedSelectionsBeforeDrawerRef.current !== null) {
      onFieldChange(
        "allowedRateRoomCodes",
        savedSelectionsBeforeDrawerRef.current
      );
      savedSelectionsBeforeDrawerRef.current = null;
    }
    setShowRoomsRateplansModal(false);
  }, [onFieldChange]);

  /**
   * Rooms + rate plans for one property, from cache when possible.
   *
   * The page already holds the data for `ratePlansPropertyId`, so that
   * property seeds the cache instead of costing a round trip. Any other
   * property is fetched. Returns null when the fetch fails.
   */
  const getOrFetchPropertyRoomRateData = useCallback(
    async (propId) => {
      if (propId == null) return null;

      const cached = roomsRateplansByPropertyRef.current[propId];
      if (cached) return cached;

      const isPageProperty =
        ratePlansPropertyId != null &&
        normalizePropertyKey(propId) ===
          normalizePropertyKey(ratePlansPropertyId);
      if (isPageProperty && rooms.length > 0 && ratePlans.length > 0) {
        const pageData = { rooms, ratePlans };
        roomsRateplansByPropertyRef.current[propId] = pageData;
        return pageData;
      }

      try {
        const [ratePlansRes, roomsRes] = await Promise.all([
          getRatePlansByProperty(propId),
          getRoomsByProperty(propId),
        ]);
        const data = {
          rooms: roomsRes?.data?.data || [],
          ratePlans: ratePlansRes?.data?.data || [],
        };
        roomsRateplansByPropertyRef.current[propId] = data;
        return data;
      } catch (error) {
        getLogger().error(LOG_SOURCE, {
          type: "Error",
          page_url: window.location.href,
          error_desc: `Failed to load rooms/rate plans for property ${propId}`,
          error_stack: error?.stack ?? null,
        });
        return null;
      }
    },
    [ratePlansPropertyId, rooms, ratePlans, roomsRateplansByPropertyRef]
  );

  /**
   * Generation counter for the all-combos rebuild, which replaces the whole
   * per-property map and reads every selected property's rooms and rate plans
   * before doing so.
   *
   * Anything that invalidates an in-flight rebuild bumps this: starting another
   * rebuild (the Properties picker's blur can fire several in a row) and every
   * pick in the All/Selected dropdown (choosing "Selected" has to be able to
   * cancel a pending "all"). A rebuild captures the value before awaiting and
   * drops its write if it no longer matches, so the newest intent wins rather
   * than whichever response happens to land last.
   */
  const combosRebuildGenerationRef = useRef(0);

  const supersedeCombosRebuild = useCallback(
    () => ++combosRebuildGenerationRef.current,
    []
  );

  // Fetch rooms/rateplans for all selected properties and build combos in "all" mode
  const fetchAndBuildCombosForProperties = useCallback(
    async (propertyIds, currentRoomsRateplansValue) => {
      // Bumped before the single-property bail-out, not after: narrowing the
      // Properties picker to one property still calls in here, and it has to
      // cancel a multi-property rebuild already in flight. Otherwise that
      // rebuild's write lands afterwards and resurrects entries for properties
      // the user just removed.
      const generation = supersedeCombosRebuild();

      if (propertyIds.length <= 1) return;

      try {
        // Fetch (or reuse cached) data for every selected property in parallel
        await Promise.allSettled(
          propertyIds.map((propId) => getOrFetchPropertyRoomRateData(propId))
        );

        // A newer rebuild or a later pick supersedes this one — its result wins.
        if (combosRebuildGenerationRef.current !== generation) return;

        // If "all" mode, auto-build combos for every selected property
        if (currentRoomsRateplansValue === "all") {
          const byProperty = {};
          propertyIds.forEach((propId) => {
            const data = roomsRateplansByPropertyRef.current[propId];
            if (data) {
              byProperty[propId] = buildAllRoomRateplanCombos(
                data.rooms,
                data.ratePlans,
                propId
              );
            }
          });
          onFieldChange("allowedRateRoomCodesByProperty", byProperty);
        }
      } catch (error) {
        // Callers fire this without awaiting, so a throw here would surface as
        // an unhandled rejection rather than anything actionable.
        getLogger().error(LOG_SOURCE, {
          type: "Error",
          page_url: window.location.href,
          error_desc:
            "Failed to rebuild room/rate plan combos for all properties",
          error_stack: error?.stack ?? null,
        });
      }
    },
    [
      getOrFetchPropertyRoomRateData,
      onFieldChange,
      supersedeCombosRebuild,
      roomsRateplansByPropertyRef,
    ]
  );

  /**
   * Select every room × rate plan of one property, fetching its data first.
   *
   * Used when the Properties picker narrows to a single property: the form
   * then behaves as if it had been opened on that property. Combos are tagged
   * with it, so they can never be submitted under a different one.
   */
  const applyDefaultCombosForProperty = useCallback(
    async (propertyId) => {
      const requestKey = normalizePropertyKey(propertyId);
      if (requestKey === null) return;

      pendingSinglePropertyRef.current = requestKey;
      onFieldChange("roomRatePlansLoading", true);

      const data = await getOrFetchPropertyRoomRateData(propertyId);

      // A newer property selection started while this one was in flight —
      // its result wins, so drop this one. The selection that superseded it
      // owns the loading flag from here on.
      if (pendingSinglePropertyRef.current !== requestKey) return;

      onFieldChange("roomRatePlansLoading", false);

      if (!data) {
        toast.error("Unable to load rooms and rate plans for this property");
        return;
      }

      const combos = buildAllRoomRateplanCombos(
        data.rooms,
        data.ratePlans,
        propertyId
      );
      if (combos.length === 0) return;

      onFieldChange("allowedRateRoomCodes", combos);
      onFieldChange("allowedRateRoomCodesByProperty", { [propertyId]: combos });

      // Prime the drawer so opening it shows this property's grid right away
      setDrawerRatePlans(data.ratePlans);
      setDrawerRooms(data.rooms);
      setDrawerDataPropertyId(propertyId);
    },
    [getOrFetchPropertyRoomRateData, onFieldChange]
  );

  // Called when properties TagPicker closes (loses focus)
  const handlePropertiesBlur = useCallback(() => {
    // Clean up cached data for deselected properties. Cache keys are strings
    // while property ids are numbers, so both sides are normalised — comparing
    // them raw evicted every entry on each blur.
    const keep = new Set(selectedProperties.map(normalizePropertyKey));
    Object.keys(roomsRateplansByPropertyRef.current).forEach((propId) => {
      if (!keep.has(normalizePropertyKey(propId))) {
        delete roomsRateplansByPropertyRef.current[propId];
      }
    });

    fetchAndBuildCombosForProperties(selectedProperties, roomsRateplansValue);
  }, [
    selectedProperties,
    roomsRateplansValue,
    fetchAndBuildCombosForProperties,
    roomsRateplansByPropertyRef,
  ]);

  /**
   * Properties TagPicker changed. Room/rate plan selections belong to the
   * property they were made against, so anything left over from a property
   * that is no longer selected has to go — otherwise it stays in
   * allowedRateRoomCodes and gets submitted under whichever property remains.
   */
  const handlePropertiesChange = useCallback(
    (nextSelectedProperties) => {
      const nextIds = Array.isArray(nextSelectedProperties)
        ? nextSelectedProperties
        : [];
      setSelectedProperties(nextIds);

      // Any default-combo fetch still in flight was started for the previous
      // selection. Invalidate it here rather than in the fetch branch alone —
      // every path out of this handler supersedes it, and leaving the marker
      // set would let a stale response overwrite the new selection and strand
      // roomRatePlansLoading at true (Launch stays disabled forever).
      if (pendingSinglePropertyRef.current !== null) {
        pendingSinglePropertyRef.current = null;
        onFieldChange("roomRatePlansLoading", false);
      }

      // Same reasoning for an all-combos rebuild in flight: it was started for
      // the previous property set and would write entries for properties that
      // are being removed right here. This is the moment the set actually
      // changes — relying on the picker's blur to supersede it leaves a window
      // where the stale write lands first (the removal is committed on change,
      // and blur may be much later or never).
      supersedeCombosRebuild();

      const keep = new Set(nextIds.map(normalizePropertyKey));

      const byProperty = promo?.allowedRateRoomCodesByProperty || {};
      const prunedByProperty = {};
      Object.entries(byProperty).forEach(([propId, codes]) => {
        if (keep.has(normalizePropertyKey(propId))) {
          prunedByProperty[propId] = codes;
        }
      });
      onFieldChange("allowedRateRoomCodesByProperty", prunedByProperty);

      // An untagged combo was built from the page's rooms/rate plans, so it
      // belongs to the property those were loaded for. Attributing it here is
      // what stops it surviving the swap: treating untagged as "matches any
      // property" left the old property's rateplan ids in the selection, and
      // they were then submitted under the newly picked property's id.
      const implicitOwner = ratePlansPropertyId ?? selectedProperty?.propertyId;
      const remainingCodes = (promo?.allowedRateRoomCodes || []).filter(
        (combo) => {
          const owner = resolveComboOwner(combo, implicitOwner);
          return owner === null || keep.has(owner);
        }
      );

      onFieldChange("allowedRateRoomCodes", remainingCodes);

      // Narrowed to a single property and nothing of its own is selected: the
      // form should behave as if it had been opened on that property. Prefer a
      // selection already saved for it this session, otherwise fetch its
      // rooms/rate plans and select them all. Without this the form is left
      // with an empty selection and the submit is rejected for a property the
      // user did pick.
      if (nextIds.length !== 1 || remainingCodes.length > 0) return;

      const onlyRemainingId = nextIds[0];
      const savedForRemaining = filterCombosForProperty(
        prunedByProperty[onlyRemainingId],
        onlyRemainingId
      );

      if (savedForRemaining.length > 0) {
        onFieldChange("allowedRateRoomCodes", savedForRemaining);
        return;
      }

      applyDefaultCombosForProperty(onlyRemainingId);
    },
    [
      promo?.allowedRateRoomCodes,
      promo?.allowedRateRoomCodesByProperty,
      ratePlansPropertyId,
      selectedProperty?.propertyId,
      onFieldChange,
      applyDefaultCombosForProperty,
      supersedeCombosRebuild,
      setSelectedProperties,
    ]
  );

  // -------------------------------------------------------------------------
  // Inline helpers (private — not returned)
  // -------------------------------------------------------------------------

  const getInitialPropertyId = () => {
    if (selectedProperties.length > 0) {
      return selectedProperties.includes(selectedProperty?.propertyId)
        ? selectedProperty?.propertyId
        : selectedProperties[0];
    }
    return selectedProperty?.propertyId || null;
  };

  const applySelections = (
    savedSelections,
    roomsData,
    ratePlansData,
    propertyId
  ) => {
    // A saved selection is only reusable for the property it was made
    // against; anything else is rebuilt from that property's own data.
    const ownSelections = filterCombosForProperty(savedSelections, propertyId);
    if (ownSelections.length > 0) {
      onFieldChange("allowedRateRoomCodes", ownSelections);
    } else if (roomsData.length > 0 && ratePlansData.length > 0) {
      const allCombos = buildAllRoomRateplanCombos(
        roomsData,
        ratePlansData,
        propertyId
      );
      onFieldChange("allowedRateRoomCodes", allCombos);
    }
  };

  const fetchAndApplyDrawerData = async (propertyId) => {
    try {
      const [ratePlansRes, roomsRes] = await Promise.all([
        getRatePlansByProperty(propertyId),
        getRoomsByProperty(propertyId),
      ]);
      const ratePlansData = ratePlansRes?.data?.data || [];
      const roomsData = roomsRes?.data?.data || [];
      setDrawerRatePlans(ratePlansData);
      setDrawerRooms(roomsData);
      setDrawerDataPropertyId(propertyId);

      const savedSelections =
        promo?.allowedRateRoomCodesByProperty?.[propertyId];
      applySelections(savedSelections, roomsData, ratePlansData, propertyId);
    } catch {
      setDrawerRatePlans(ratePlans || []);
      setDrawerRooms(rooms || []);
      setDrawerDataPropertyId(ratePlansPropertyId);
    }
  };

  const initDrawerForLocalProperty = (initialPropertyId) => {
    setDrawerRatePlans(ratePlans || []);
    setDrawerRooms(rooms || []);
    setDrawerDataPropertyId(ratePlansPropertyId);

    // The grid shows the page data, so combos built here belong to the
    // property that data was fetched for — not to whatever is selected.
    const dataPropertyId = ratePlansPropertyId ?? initialPropertyId;

    if (selectedProperties.length > 1) {
      const savedSelections =
        promo?.allowedRateRoomCodesByProperty?.[initialPropertyId];
      applySelections(savedSelections, rooms, ratePlans, dataPropertyId);
    } else if (isMarket) {
      const currentSelections = promo?.allowedRateRoomCodes || [];
      if (currentSelections.length === 0) {
        applySelections(null, rooms, ratePlans, dataPropertyId);
      }
    } else {
      const savedSelections = promo?.allowedRateRoomCodes || [];
      applySelections(savedSelections, rooms, ratePlans, dataPropertyId);
    }
  };

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Keep drawer data in sync when parent props change
  useEffect(() => {
    setDrawerRatePlans(ratePlans || []);
  }, [ratePlans]);

  useEffect(() => {
    setDrawerRooms(rooms || []);
  }, [rooms]);

  // The grid falls back to the page data, so it is showing the page's
  // property whenever that data is replaced.
  useEffect(() => {
    setDrawerDataPropertyId(ratePlansPropertyId);
  }, [ratePlansPropertyId]);

  // Extract dependency values for useEffect.
  // Counts come from the drawer data, which tracks the property the current
  // selection belongs to. Using the page counts marked a fully-selected other
  // property as "Selected Rooms & Rate Plans" whenever its room/rate plan
  // count differed from the page property's.
  const allowedRateRoomCodesLength = (promo?.allowedRateRoomCodes || []).length;
  const roomsLength = drawerRooms?.length || 0;
  const ratePlansLength = drawerRatePlans?.length || 0;

  // Update roomsRateplansValue when promo changes
  useEffect(() => {
    const currentSelectedRoomsRateplans = promo?.allowedRateRoomCodes || [];
    const currentKey = JSON.stringify(currentSelectedRoomsRateplans);

    if (selectedRoomsRateplansKeyRef.current !== currentKey) {
      selectedRoomsRateplansKeyRef.current = currentKey;
      selectedRoomsRateplansRef.current = currentSelectedRoomsRateplans;

      if (forceSelectOnly) {
        setRoomsRateplansValue("select");
      } else if (currentSelectedRoomsRateplans.length === 0) {
        setRoomsRateplansValue("all");
      } else {
        const totalCombos = roomsLength * ratePlansLength;
        setRoomsRateplansValue(
          currentSelectedRoomsRateplans.length === totalCombos
            ? "all"
            : "select"
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRateRoomCodesLength, roomsLength, ratePlansLength]);

  /**
   * The user picked a value in the "All / Selected Rooms & Rate Plans" dropdown.
   *
   * Choosing "all" with several properties selected rebuilds every property's
   * combos so the payload covers them all. That rebuild replaces the whole
   * per-property map, so it must happen *only* on an explicit user pick — the
   * effect above also derives "all" whenever the working selection happens to
   * cover every cell, and switching hotels in the drawer produces exactly that
   * (an unvisited hotel defaults to all of its combos). Rebuilding there
   * discarded selections the user had already saved for the other hotels.
   *
   * The rebuild is invoked straight from this handler rather than from an effect
   * watching `roomsRateplansValue`. An effect cannot tell the two cases apart
   * without a "was this the user?" ref, and such a ref goes stale: picking the
   * value that is already selected is a no-op setState, so the effect never runs
   * to clear it, and the next *derived* switch to "all" would consume the stale
   * flag and rebuild after all.
   */
  const handleRoomsRateplansValueChange = useCallback(
    (value) => {
      // Bump before dispatching so any rebuild already in flight — including one
      // started by the Properties picker's blur — sees itself superseded and
      // drops its write. Every pick counts, not just "all": picking "Selected"
      // has to be able to cancel a pending "all" rebuild.
      supersedeCombosRebuild();

      setRoomsRateplansValue(value);

      if (value === "all" && selectedProperties.length > 1) {
        fetchAndBuildCombosForProperties(selectedProperties, "all");
      }
    },
    [
      selectedProperties,
      fetchAndBuildCombosForProperties,
      supersedeCombosRebuild,
    ]
  );

  // When isMarket is true, automatically select all rooms and rate plans
  useEffect(() => {
    if (isMarket) {
      const currentCombos = promo?.allowedRateRoomCodes || [];
      if (currentCombos.length > 0) {
        onFieldChange("allowedRateRoomCodes", []);
      }
      if (roomsRateplansValue !== "all") {
        setRoomsRateplansValue("all");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarket]);

  // Update selectedHotelInDrawer when drawer opens
  useEffect(() => {
    const drawerJustOpened =
      showRoomsRateplansModal && !prevDrawerOpenRef.current;

    if (showRoomsRateplansModal && drawerJustOpened) {
      const initialPropertyId = getInitialPropertyId();
      setSelectedHotelInDrawer(initialPropertyId);
      setSuccessMessage(null);

      savedSelectionsBeforeDrawerRef.current = [
        ...(promo?.allowedRateRoomCodes || []),
      ];
      setDrawerSelections([...(promo?.allowedRateRoomCodes || [])]);

      const needsApiFetch =
        initialPropertyId && initialPropertyId !== selectedProperty?.propertyId;

      if (needsApiFetch) {
        fetchAndApplyDrawerData(initialPropertyId);
      } else {
        initDrawerForLocalProperty(initialPropertyId);
      }
    }

    prevDrawerOpenRef.current = showRoomsRateplansModal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showRoomsRateplansModal,
    selectedProperty?.propertyId,
    selectedProperties.length,
    onFieldChange,
  ]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------
  return {
    // State
    showRoomsRateplansModal,
    setShowRoomsRateplansModal,
    drawerSelections,
    setDrawerSelections,
    drawerRatePlans,
    setDrawerRatePlans,
    drawerRooms,
    setDrawerRooms,
    drawerDataPropertyId,
    setDrawerDataPropertyId,
    selectedHotelInDrawer,
    setSelectedHotelInDrawer,
    successMessage,
    setSuccessMessage,
    roomsRateplansValue,
    // Deliberately not exporting the raw setter: it bypasses the rebuild that
    // an explicit "all" pick has to trigger.
    handleRoomsRateplansValueChange,

    // Refs (public subset)
    savedSelectionsBeforeDrawerRef,
    roomsRateplansByPropertyRef,

    // Memos
    roomsRateplansOptions,

    // Handlers
    handleDrawerClose,
    handlePropertiesBlur,
    handlePropertiesChange,
  };
}
